import { ComponentProps, SyntheticEvent, useEffect, useState } from 'react'
import { Alert, Snackbar, SnackbarCloseReason, Stack } from '@mui/material'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { XIcon } from '@phosphor-icons/react'
import { tokens, useErrorSnackbar } from 'ComponentLibrary'
import { AlertOctagon } from 'react-feather'

type ErrorSnackbarProps = Pick<ComponentProps<typeof Snackbar>, 'anchorOrigin' | 'autoHideDuration'>

export const ErrorSnackbar = ({ autoHideDuration, anchorOrigin }: ErrorSnackbarProps) => {
    const { hasError, clearError, error } = useErrorSnackbar()

    const [open, setOpen] = useState(false) // Keep the visual open/close state locally so we can wait for the exit animation to finish before wiping the error from context.

    useEffect(
        function syncSnackbarStateWithError() {
            setOpen(hasError)
        },
        [hasError]
    )

    function handleClose(_?: Event | SyntheticEvent<unknown, Event>, reason?: SnackbarCloseReason) {
        if (reason === 'clickaway') {
            // To prevent users from accidentally clicking away the toast.
            return
        }
        setOpen(false)
    }

    return (
        <Snackbar
            anchorOrigin={anchorOrigin}
            open={open}
            onClose={handleClose}
            autoHideDuration={autoHideDuration}
            TransitionProps={{ onExited: () => clearError() }}
            style={{ zIndex: 1700 }}
        >
            <Alert
                iconMapping={{
                    error: <AlertOctagon />, // For consistency with our Alert component, at the time of writing using our alert component lead to the app crashing, thus using mui's alert for now
                }}
                action={[
                    <IconButton
                        variant="tertiary"
                        key="close"
                        onClick={handleClose}
                        icon={<XIcon color={tokens.ColorIconSubtle} weight="bold" />}
                        size="medium"
                    />,
                ]}
                severity="error"
            >
                <Stack direction={'column'}>
                    <Typography variant={'textS'}>{error?.headline}</Typography>
                    {error?.detail && <Typography variant={'textXS'}>{error.detail}</Typography>}
                </Stack>
            </Alert>
        </Snackbar>
    )
}
